import {
  AlignedPrograms,
  EducationLevelEnum,
  Institution,
  InstructionalProgram,
  LivingConditionTypeEnum,
  Location,
  Occupation,
  PercentileCareerDistribution,
  RefineEducationCost,
  ResidencyTypeEnum,
  RocCalculatorOutput
} from '@lib/utils';
import { Entity, Guard, Result } from '@vantage-point/ddd-core';

import { RocCalculatorInput } from '../../models';

import { RocModelId } from './roc-model-id';
import { RocModelBusinessRules } from './roc-model.business-rules';

export interface RocModelProps {
  name: string;
  defaultName: string;

  goalSalaryAnalysis: PercentileCareerDistribution | null | undefined;
  locationModel: Location | null | undefined;
  occupationModel: Occupation | null | undefined;
  degreeLevel: EducationLevelEnum | null | undefined;
  degreeProgram: InstructionalProgram | null | undefined;
  retirementAge: number | null | undefined;

  alignedPrograms: AlignedPrograms | null | undefined;
  institutionModel: Institution | null | undefined;
  startYear: number | null | undefined;
  isFulltime: boolean | null | undefined;
  yearsToCompleteDegree: number | null | undefined;

  residencyType: ResidencyTypeEnum | null | undefined;
  livingConditionTypeEnum: LivingConditionTypeEnum | null | undefined;

  isTaxDependent: boolean | null | undefined;
  prefersIncomeBasedRepayment: boolean | null | undefined;
  outOfPocketExpensesByYear: number[] | null | undefined;
  federalSubsidizedLoanAmountByYear: number[] | null | undefined;
  federalUnsubsidizedLoanAmountByYear: number[] | null | undefined;
  federalLoanAmountByYear: number[] | null | undefined;
  privateLoanAmountByYear: number[] | null | undefined;
  yearsToPayOffFederalLoan: number | null | undefined;
  yearsToPayOffPrivateLoan: number | null | undefined;

  radiusInMiles?: number | null | undefined;

  currentStateInput?: RocCalculatorInput | null | undefined;
  currentStateOutput?: RocCalculatorOutput | null | undefined;
  currentStateHash?: string | null | undefined;
  goalStateInput?: RocCalculatorInput | null | undefined;
  goalStateOutput?: RocCalculatorOutput | null | undefined;
  goalStateHash?: string | null | undefined;

  refineEducationCost: RefineEducationCost | null | undefined;
  eligiblePellAmountByYear: number[] | null | undefined;
  isEligibleForSubsidizedLoanByYear: boolean[] | null | undefined;
}

export class RocModel extends Entity<RocModelProps> {
  /* #region  PROPERTIES */

  private _rocModelId: RocModelId;

  get name(): string {
    return this.props.name;
  }

  get goalSalaryAnalysis(): PercentileCareerDistribution | null | undefined {
    return this.props.goalSalaryAnalysis;
  }

  get locationModel(): Location | null | undefined {
    return this.props.locationModel;
  }

  get occupationModel(): Occupation | null | undefined {
    return this.props.occupationModel;
  }

  get degreeLevel(): EducationLevelEnum | null | undefined {
    return this.props.degreeLevel;
  }

  get degreeProgram(): InstructionalProgram | null | undefined {
    return this.props.degreeProgram;
  }

  get retirementAge(): number | null | undefined {
    return this.props.retirementAge;
  }

  get institutionModel(): Institution | null | undefined {
    return this.props.institutionModel;
  }

  get alignedPrograms(): AlignedPrograms | null | undefined {
    return this.props.alignedPrograms;
  }

  get institutionName(): string | null | undefined {
    return this.props.institutionModel?.name;
  }

  get startYear(): number | null | undefined {
    return this.props.startYear;
  }

  get isFulltime(): boolean | null | undefined {
    return this.props.isFulltime;
  }

  get yearsToCompleteDegree(): number | null | undefined {
    return this.props.yearsToCompleteDegree;
  }

  get fullTimeStudentPercent(): number | null | undefined {
    return this.props.isFulltime ?? true ? 1 : 0.5;
  }

  get residencyType(): ResidencyTypeEnum | null | undefined {
    return this.props.residencyType;
  }

  get livingConditionTypeEnum(): LivingConditionTypeEnum | null | undefined {
    return this.props.livingConditionTypeEnum;
  }

  get isTaxDependent(): boolean | null | undefined {
    return this.props.isTaxDependent;
  }

  get prefersIncomeBasedRepayment(): boolean | null | undefined {
    return this.props.prefersIncomeBasedRepayment;
  }

  get outOfPocketExpensesByYear(): number[] | null | undefined {
    return this.props.outOfPocketExpensesByYear;
  }

  get federalSubsidizedLoanAmountByYear(): number[] | null | undefined {
    return this.props.federalSubsidizedLoanAmountByYear;
  }

  get federalUnsubsidizedLoanAmountByYear(): number[] | null | undefined {
    return this.props.federalUnsubsidizedLoanAmountByYear;
  }

  get federalLoanAmountByYear(): number[] | null | undefined {
    return this.props.federalLoanAmountByYear;
  }

  get privateLoanAmountByYear(): number[] | null | undefined {
    return this.props.privateLoanAmountByYear;
  }

  get yearsToPayOffFederalLoan(): number | null | undefined {
    return this.props.yearsToPayOffFederalLoan;
  }

  get yearsToPayOffPrivateLoan(): number | null | undefined {
    return this.props.yearsToPayOffPrivateLoan;
  }

  get refineEducationCost(): RefineEducationCost | null | undefined {
    return this.props.refineEducationCost;
  }

  get isEligibleForSubsidizedLoanByYear(): boolean[] | null | undefined {
    return this.props.isEligibleForSubsidizedLoanByYear;
  }

  /* #endregion */

  private constructor(props: RocModelProps, roiModelId?: RocModelId) {
    super(props, roiModelId?.id);

    this._rocModelId = RocModelId.create(this._id);
  }

  public static create(props: RocModelProps, id?: RocModelId): Result<RocModel> {
    const propsResult = Guard.againstNullOrUndefinedBulk([]);

    if (!propsResult.succeeded) {
      return Result.failure<RocModel>(propsResult.message || 'roi model properties error');
    }

    props = RocModelBusinessRules.runAllSelfValidationRules(props);

    const model = new RocModel(
      {
        ...props
      },
      id
    );

    return Result.success<RocModel>(model);
  }
}
